import type { TranslationDictionary } from "./schema.ts";

import { zhCNAgentGui } from "./zh-CN.agentGui.ts";
import { zhCNMessages } from "./zh-CN.messages.ts";
import { zhCNSettingsPanel } from "./zh-CN.settingsPanel.ts";
import { zhCNWebsiteNode } from "./zh-CN.websiteNode.ts";
import { zhCNWorkspaceAgentProbeQuota } from "./zh-CN.workspaceAgentProbeQuota.ts";
export const zhCN = {
  common: {
    add: "添加",
    cancel: "取消",
    clear: "清除",
    close: "关闭",
    confirm: "确认",
    copy: "复制",
    copying: "复制中...",
    copyFailed: "复制失败",
    create: "创建",
    cut: "剪切",
    delete: "删除",
    deleting: "删除中...",
    download: "下载",
    copyImage: "复制图片",
    downloadImage: "下载图片",
    expandImage: "放大图片",
    imageZoomPercent: "图片缩放 {{percent}}%",
    error: "错误",
    generateByAi: "AI 生成",
    generating: "生成中...",
    info: "提示",
    loading: "加载中...",
    maximize: "最大化",
    minimize: "最小化",
    minimizeImage: "缩小图片",
    resetImageZoom: "重置图片缩放",
    refresh: "刷新",
    remove: "移除",
    removing: "移除中...",
    restore: "还原",
    zoomInImage: "放大图片内容",
    zoomOutImage: "缩小图片内容",
    resetToDefault: "恢复默认",
    save: "保存",
    saving: "保存中...",
    settings: "设置",
    warning: "警告",
    defaultFollowCli: "默认值（跟随 CLI）",
    defaultModel: "默认模型",
    followCliDefault: "跟随 CLI 默认值",
    unknownError: "未知错误",
    notAvailable: "无",
    paste: "粘贴",
    percentUnit: "%",
    pixelUnit: "px",
    minuteUnit: "分钟"
  },
  workspaceWindowLayout: {
    trigger: "窗口布局",
    moveAndResize: "布局",
    left: "左对齐",
    right: "右对齐",
    top: "上对齐",
    bottom: "下对齐",
    fullscreen: "全屏幕",
    restore: "还原"
  },
  sidebar: {
    defaultAgent: "默认 Agent",
    persistence: "持久化",
    settings: "设置",
    fallbackAgentLabel: "Agent",
    status: {
      working: "运行中",
      standby: "待命"
    },
    terminals_one: "{{count}} 个终端",
    terminals_other: "{{count}} 个终端",
    agents_one: "{{count}} 个 Agent",
    agents_other: "{{count}} 个 Agent",
    tasks_one: "{{count}} 个任务",
    tasks_other: "{{count}} 个任务"
  },
  appHeader: {
    togglePrimarySidebar: "切换主侧边栏",
    commandCenter: "命令中心",
    commandCenterHint: "命令中心（{{shortcut}}）",
    commandCenterFallbackTitle: "搜索",
    enterFullscreen: "进入全屏",
    exitFullscreen: "退出全屏",
    updateAvailableShort: "更新",
    updateAvailableTitle: "发现新版本 {{version}}",
    updateAvailableDetail: "准备好时再开始下载，不会打断当前使用。",
    updateLater: "稍后",
    updateDownloadNow: "下载更新",
    updateDownloadingTitle: "正在下载 {{version}}（{{percent}}）",
    updateDownloadingShort: "下载更新",
    updateDownloadProgress: "{{downloaded}} / {{total}}",
    updateMinimize: "最小化",
    restartToUpdateShort: "重启更新",
    restartToUpdateTitle: "版本 {{version}} 已准备好安装",
    updateReadyDetail: "重启应用后即可完成本次更新安装。",
    updateRestartLater: "稍后重启",
    updateInstallNow: "重启并安装"
  },
  controlCenter: {
    open: "控制中心",
    title: "控制中心",
    sidebar: "侧边栏",
    theme: "主题",
    agentStandbyBanner: "Agent 完成提醒",
    on: "已开启",
    off: "已关闭"
  },
  debugWindow: {
    title: "IPC 检查器",
    subtitle: "用于查看渲染进程到主进程 IPC trace 的隐藏诊断窗口。",
    loading: "正在加载 IPC 记录…",
    unavailable: "调试窗口 bridge 不可用。",
    searchPlaceholder: "搜索 channel、payload、result 或 error",
    countLabel: "{{count}} 条记录",
    empty: "还没有捕获到 IPC 记录。",
    noSelection: "选择一条记录以查看详情。",
    actions: {
      pin: "置顶",
      unpin: "取消置顶",
      clear: "清空记录",
      export: "导出 JSON",
      copySelected: "复制当前记录"
    },
    filters: {
      kindLabel: "类型",
      kindAll: "全部",
      kindInvoke: "Invoke",
      kindSend: "Send",
      statusLabel: "状态",
      statusAll: "全部",
      statusOk: "成功",
      statusError: "错误",
      statusSent: "已发送"
    },
    detail: {
      summary: "摘要",
      payload: "Payload",
      result: "Result",
      error: "Error",
      channel: "Channel",
      kind: "类型",
      status: "状态",
      startedAt: "开始时间",
      completedAt: "结束时间",
      duration: "耗时",
      durationValue: "{{duration}} 毫秒"
    }
  },
  commandCenter: {
    title: "命令中心",
    placeholder: "搜索房间和命令…",
    empty: "没有结果。",
    metaEsc: "Esc",
    sections: {
      commands: "命令"
    },
    commands: {
      openSettings: "设置",
      openSettingsHint: "打开设置",
      showPrimarySidebar: "显示侧边栏",
      hidePrimarySidebar: "隐藏侧边栏",
      togglePrimarySidebarHint: "切换主侧边栏"
    }
  },
  workspaceEmptyState: {
    title: "创建一个房间开始使用",
    description: "每个房间都有自己的协作画布和终端。",
    action: "创建房间"
  },
  appMessage: {
    info: "提示",
    warning: "警告",
    error: "错误"
  },
  errorBoundary: {
    title: "出现异常",
    description: "渲染进程遇到了不可恢复的错误。你的房间数据仍然安全。",
    reload: "重新加载",
    dismiss: "忽略"
  },
  agentRuntime: {
    working: "运行中",
    standby: "待命",
    exited: "已退出",
    failed: "失败",
    stopped: "已停止",
    restoring: "启动中"
  },
  persistence: {
    savedWithoutScrollback: "存储配额已达上限；已保存，但未包含终端历史。",
    savedSettingsOnly: "存储配额已达上限；仅保存了设置。",
    unavailable: "存储不可用；更改将不会被保存。",
    limitExceeded: "超出存储限制；无法持久化房间状态。",
    ioFailed: "持久化 I/O 失败：{{message}}",
    failed: "持久化失败：{{message}}",
    recoveryCorruptDb: "持久化数据库已损坏，现已重置。",
    recoveryMigrationFailed: "持久化迁移失败，现已重置。"
  },
  agentHost: {
    backToHome: "返回首页",
    shellHostWindow: {
      windowControls: "窗口控制"
    },
    changeWallpaper: "更换壁纸",
    wallpaperPanelTitle: "壁纸",
    wallpaperOptionDefault: "默认",
    wallpaperOptionOcean: "海面",
    wallpaperOptionGalaxy: "星系",
    wallpaperOptionSky: "云层",
    wallpaperOptionPeaks: "雪峰夜空",
    wallpaperOptionOrbit: "地球夜景",
    wallpaperOptionSand: "流沙纹理",
    wallpaperOptionDunes: "星夜沙丘",
    signOut: "退出登录",
    loggedOut: "已退出登录。",
    sessionExpired: "登录已失效，请重新登录。",
    authenticating: "处理中…",
    loginTitle: "登录以继续",
    loginSectionTitle: "使用你的账号继续",
    loginDescription: "选择一种登录方式，在浏览器完成登录后，再返回 Tutti。",
    loginBrowserHint: "选择一种登录方式，在浏览器完成认证后，再返回 Tutti。",
    loginOpeningBrowser: "正在打开浏览器...",
    loginContinueWithGoogle: "使用 Google 继续",
    loginContinueWithGitHub: "使用 GitHub 继续",
    loginGitHubHint:
      "GitHub 可能会直接复用浏览器里已经登录的账号，不一定会出现选账号页面。",
    emailLoginTitle: "或者使用邮箱验证码",
    loginDividerOr: "或邮箱",
    loginAltMethodsAria: "更多登录方式",
    loginAltTabEmail: "邮箱验证码",
    loginAltTabLoginCode: "访问码",
    loginCodeTitle: "或使用访问码",
    loginCodeHint: "直接输入长期有效的访问码，即可在当前设备完成登录。",
    loginCodePlaceholder: "请输入访问码",
    verifyLoginCode: "确认",
    loginCodeCompleted: "访问码登录完成。",
    loginCodeRequired: "请输入访问码。",
    emailPlaceholder: "name@example.com",
    emailCodePlaceholder: "6 位验证码",
    emailCodeDialogTitle: "输入验证码",
    emailCodeDialogLead: "验证码已发送至 {{email}}",
    emailCodeDigitAria: "验证码第 {{index}} 位",
    emailCodeResendPrefix: "没有收到验证码？",
    emailCodeResendAction: "重新发送",
    sendEmailCode: "发送验证码",
    sendEmailCodeSending: "发送中...",
    verifyEmailCode: "验证并登录",
    emailCodeSent: "验证码已发送。",
    emailLoginCompleted: "邮箱登录完成。",
    emailRequired: "请输入邮箱地址。",
    emailCodeRequired: "请输入验证码。",
    desktopAuthBridgeStale: "桌面端认证桥接已过期，请重启 Tutti 后重试。",
    desktopActionErrorGeneric: "出错了，请稍后重试。",
    desktopActionErrorGenericShort: "出错了",
    desktopActionErrorUnavailable: "该功能暂不可用。",
    desktopActionErrorLoginTimedOut: "登录已超时，请返回 Tutti 后重试。",
    desktopActionErrorDesktopApiUnavailable:
      "桌面端服务正在更新，请重启 Tutti 后重试。",
    desktopActionErrorPackageDownloadInterrupted:
      "智能体安装包下载中断。请检查网络后点击重试。",
    desktopActionErrorPackageDownloadHTTPStatus:
      "安装包服务拒绝了下载请求。请稍后重试，或联系支持人员。",
    desktopActionErrorPackageDownloadInvalid:
      "下载的安装包未通过完整性校验。请重新下载。",
    desktopActionErrorPackageDownloadDisk:
      "无法写入安装包缓存。请检查磁盘空间和目录权限。",
    productNameCanvas: "Tutti",
    applications: {
      eyebrow: "Agent OS 应用",
      title: "应用中心",
      description: "从一个入口打开房间关键应用，并预览 Agent OS 常用工作流",
      launchHint: "Mock 应用进入房间后即可打开",
      launchMockUnavailable: "进入房间后可以打开这个 Mock 应用",
      categoryBasic: "基础",
      categoryOffice: "办公",
      categoryCreation: "创作",
      installAction: "安装",
      comingSoonAction: "即将上线",
      installedAction: "已安装",
      issueTitle: "任务中心",
      issueDescription: "创建、分派、运行并验收房间内的任务",
      vibeDesignTitle: "Vibe 设计",
      vibeDesignDescription: "生成产品界面、交互状态和视觉方向草案",
      vibeVideoTitle: "视频创作",
      vibeVideoDescription: "组织脚本、分镜、剪辑和最终视频方向",
      imageGenerationTitle: "图片生成",
      imageGenerationDescription: "创建视觉概念稿、生成图片素材",
      textEditorTitle: "文档",
      textEditorDescription: "编写和整理房间笔记、提示词与轻量文档",
      pptTitle: "PPT",
      pptDescription: "创建演示大纲、幻灯片草稿",
      sheetTitle: "表格",
      sheetDescription: "整理结构化数据、表格、计算和房间计划清单",
      calendarTitle: "日程",
      calendarDescription: "规划房间里程碑、跟进任务、评审和共享日程",
      systemMonitorTitle: "系统监控",
      systemMonitorDescription: "查看运行时健康度、资源信号和工作区活动",
      codeEditorTitle: "代码编辑器",
      codeEditorDescription: "打开源码文件、审阅变更并整理实现备注",
      chatTitle: "聊天",
      chatDescription: "协同房间对话、Agent 交接和快速决策",
      mockWindowStatus: "Mock 应用",
      mockWindowReady: "等待后续接入",
      mockDesignPreviewTitle: "设计看板",
      mockDesignPreviewBody: "这里将展示布局参考、组件状态和评审备注",
      mockVideoPreviewTitle: "视频时间线",
      mockVideoPreviewBody: "这里将展示场景、片段、旁白和导出状态",
      mockImageGenerationPreviewTitle: "图片工作室",
      mockImageGenerationPreviewBody:
        "这里将展示提示词、生成预览、变体和导出记录",
      mockEditorPreviewTitle: "草稿文档",
      mockEditorPreviewBody:
        "可以在这个占位编辑器里组织房间笔记和 Agent 提示词",
      mockPresentationPreviewTitle: "幻灯片 Deck",
      mockPresentationPreviewBody: "这里将展示幻灯片大纲、叙事节奏和评审备注",
      mockSheetPreviewTitle: "数据表格",
      mockSheetPreviewBody: "这里将展示表格、计算、筛选和房间计划数据",
      mockCalendarPreviewTitle: "房间日历",
      mockCalendarPreviewBody: "这里将展示里程碑、会议、跟进任务和共享日程块",
      mockMonitorPreviewTitle: "运行时信号",
      mockMonitorPreviewBody: "这里将展示 CPU、内存、网络和工作区活动信号",
      mockCodePreviewTitle: "源码工作区",
      mockCodePreviewBody: "这里将展示文件、Diff、符号和实现备注",
      mockChatPreviewTitle: "房间聊天",
      mockChatPreviewBody: "这里将展示对话线程、Agent 回复和共享决策"
    },
    workspaceTerminalsBadge_one: "{{count}} 个终端",
    workspaceTerminalsBadge_other: "{{count}} 个终端",
    workspaceCenterSearchAria: "打开房间搜索",
    workspaceCenterSearchTitle: "在当前房间搜索节点、分区与便签",
    agentGui: zhCNAgentGui,
    workspaceInsights: {
      runtimeTitle: "运行时",
      live: "运行中",
      treeTitle: "目录树",
      treeLead: "与运行时一致的可见房间结构。",
      root: "根路径：",
      sandbox: "沙箱：",
      sessions: "会话：",
      provider: "提供方："
    },
    workspaceFileManager: {
      breadcrumbsAria: "面包屑导航",
      breadcrumbRoot: "首页",
      scrollBreadcrumbsLeft: "向左滚动面包屑",
      scrollBreadcrumbsRight: "向右滚动面包屑",
      previewPaneResizeAria: "调整预览面板宽度",
      name: "名称",
      modified: "修改时间",
      size: "大小",
      empty: "当前文件夹为空",
      open: "打开",
      view: "查看",
      openInBrowser: "在浏览器中打开",
      newFile: "新建文件",
      newFolder: "新建文件夹",
      upload: "上传",
      uploadHere: "上传到这里",
      downloadFile: "下载文件",
      downloadArchive: "下载压缩包",
      delete: "删除",
      deleting: "正在删除…",
      back: "后退",
      forward: "前进",
      refresh: "刷新",
      retry: "重试",
      refreshed: "文件列表已刷新。",
      uploaded: "已上传 {{count}} 项。",
      uploadNoSelection: "未选择本地文件。",
      uploadFilteredSymlinks: "已上传可用内容，符号链接已过滤。",
      uploadOnlySymlinks: "选择的内容均为符号链接，上传已取消。",
      uploadFilteredIgnored: "已过滤忽略内容后上传。",
      uploadOnlyIgnored: "过滤后没有可上传文件，上传已取消。",
      uploadFilterTitle: "检测到 .gitignore",
      uploadFilterBody:
        "默认会过滤被忽略的文件，以及常见依赖、构建产物和缓存目录。符号链接始终会被过滤。",
      uploadFilterAdvanced: "高级设置",
      uploadFilterIncludeIgnored: "上传 .gitignore 忽略的文件",
      uploadFilterIncludeIgnoredHint:
        "这可能包含依赖、构建产物和缓存文件，上传体积和工作区空间占用会增加。",
      uploadFilterSubmit: "开始上传",
      uploadConflictTitle: "检测到同名项目",
      uploadConflictBody:
        "继续上传会覆盖同名文件。同名文件夹会合并，相同相对路径的文件会被覆盖，其他已有内容会保留。",
      uploadConflictMore: "另有 {{count}} 项",
      uploadConflictSubmit: "继续上传",
      downloaded: "下载完成。",
      downloadCanceled: "已取消下载。",
      deleted: "已删除 {{name}}。",
      createdFile: "已创建 {{name}}。",
      createdFolder: "已创建 {{name}}。",
      createNameRequired: "请输入名称后再创建。",
      createNameInvalid: "名称不能包含斜杠。",
      copyPath: "复制地址",
      pathCopied: "路径已复制。",
      copyPathFailed: "复制路径失败：{{message}}",
      unsupportedViewTitle: "暂不支持预览",
      unsupportedViewBody: "下载 {{name}} 后可在本地打开。",
      operationFailed: "{{message}}",
      filePlaceholder: "notes.md",
      folderPlaceholder: "folder-name",
      cancel: "取消",
      create: "创建",
      deleteConfirm: "确定删除 {{name}}？此操作不可撤销。",
      dropToUpload: "拖放以上传到 {{path}}",
      dropToMove: "松手以移动到 {{path}}",
      previewEmpty: "选择文件或文件夹查看详情",
      previewDirectory: "双击左侧文件夹进入下一级",
      previewUnsupported: "此文件暂不支持预览。",
      previewLoading: "正在加载预览...",
      previewReadFailed: "无法加载预览：{{message}}",
      previewTooLarge: "这个文件太大，无法预览。最大 {{maxSize}}。",
      previewBinary: "这个文件看起来是二进制文件，无法在这里预览。",
      previewDecodeFailed: "这个文件不是有效的 UTF-8。",
      minimize: "最小化",
      maximize: "最大化",
      restore: "还原",
      close: "关闭"
    },
    workspaceFileNode: {
      loading: "正在加载文件...",
      readFailed: "无法读取文件：{{message}}",
      unsupportedImage: "不支持的图片格式。",
      tooLarge: "这个文件太大，无法在这里编辑。最大 {{maxSize}}。",
      binary: "这个文件看起来是二进制文件，无法在这里编辑。",
      decodeFailed: "这个文件不是有效的 UTF-8。",
      save: "保存",
      saving: "正在保存...",
      saved: "已保存",
      dirty: "有未保存更改",
      saveFailed: "保存失败：{{message}}",
      closeUnsavedTitle: "关闭前保存更改？",
      closeUnsavedBody: "{{name}} 有未保存更改。",
      saveAndClose: "保存并关闭",
      discard: "放弃",
      cancel: "取消"
    },
    workspaceRenameHint: "点击修改房间名称",
    workspaceRenameLabel: "房间名称",
    workspaceBack: "返回",
    workspaceAgentMessageCenterTitle: "Agent 消息",
    workspaceAgentMessageCenterOpenAria: "打开 Agent 消息",
    workspaceAgentMessageCenterWaitingCount_one: "{{count}} 个等待中",
    workspaceAgentMessageCenterWaitingCount_other: "{{count}} 个等待中",
    workspaceAgentMessageCenterFilterAll: "全部",
    workspaceAgentMessageCenterFilterWaiting: "等待中",
    workspaceAgentMessageCenterFilterWorking: "运行中",
    workspaceAgentMessageCenterFilterCompleted: "已完成",
    workspaceAgentMessageCenterFilterFailed: "错误",
    workspaceAgentMessageCenterViewOptions: "视图选项",
    workspaceAgentMessageCenterViewSummary: "按{{group}}分组 · {{filters}}",
    workspaceAgentMessageCenterSummaryCount: "{{count}} 条消息",
    workspaceAgentMessageCenterSummaryWaiting:
      "{{count}} 条 · {{waiting}} 待处理",
    workspaceAgentMessageCenterSummaryCompleted:
      "{{count}} 条 · {{completed}} 已完成",
    workspaceAgentMessageCenterSummaryFiltered:
      "已筛选 · {{count}}/{{total}} 条",
    workspaceAgentMessageCenterFilterActive: "已启用筛选",
    workspaceAgentMessageCenterGroupBy: "分组方式",
    workspaceAgentMessageCenterGroupPriority: "重要性",
    workspaceAgentMessageCenterGroupStatus: "状态",
    workspaceAgentMessageCenterGroupAgent: "智能体",
    workspaceAgentMessageCenterGroupTime: "时间",
    workspaceAgentMessageCenterGroupNeedsAttention: "需关注",
    workspaceAgentMessageCenterGroupRecentlyCompleted: "最近完成",
    workspaceAgentMessageCenterGroupToday: "今天",
    workspaceAgentMessageCenterGroupYesterday: "昨天",
    workspaceAgentMessageCenterGroupPreviousSevenDays: "过去 7 天",
    workspaceAgentMessageCenterGroupOlder: "更早",
    workspaceAgentMessageCenterFilterStatus: "筛选状态",
    workspaceAgentMessageCenterFilterAgent: "筛选智能体",
    workspaceAgentMessageCenterClearFilters: "清除筛选",
    workspaceAgentMessageCenterStatusQuickFilterAria:
      "查看{{status}}消息（{{count}}）",
    workspaceAgentMessageCenterExpandStackAria: "展开 {{count}} 条折叠消息",
    workspaceAgentMessageCenterCollapseStackAria: "收起已展开的消息",
    workspaceAgentMessageCenterStackSummaryCount: "{{count}} 条消息",
    workspaceAgentMessageCenterFilteredEmpty: "没有符合筛选条件的消息",
    workspaceAgentMessageCenterEmpty: "暂无 Agent 消息",
    workspaceAgentMessageCenterNoSummary: "暂无 Agent 消息",
    workspaceAgentMessageCenterOpenChat: "打开会话",
    workspaceAgentsNoSessions: "暂无会话",
    workspaceAgentsNoActivities: "暂无进行中的操作",
    workspaceAgentsUntitledConversation: "未命名对话",
    workspaceAgentsGenericAgentName: "智能体",
    workspaceAgentsSessionCount_one: "{{count}} 个会话",
    workspaceAgentsSessionCount_other: "{{count}} 个会话",
    workspaceAgentsMemberCount_one: "{{count}} 位成员",
    workspaceAgentsMemberCount_other: "{{count}} 位成员",
    workspaceAgentsSummaryWorking: "工作中",
    workspaceAgentsSummaryNeedsAttention: "需关注",
    workspaceAgentsSummaryOffDuty: "离线",
    workspaceAgentsSummaryRowAria: "成员数、工作中与需关注",
    workspaceAgentsParallelSessions_one: "{{count}} 个并行",
    workspaceAgentsParallelSessions_other: "{{count}} 个并行",
    workspaceAgentStatusWorking: "工作中",
    workspaceAgentStatusPaused: "已暂停",
    workspaceAgentStatusWaiting: "等待中",
    workspaceAgentStatusReady: "就绪",
    workspaceAgentStatusCompleted: "已完成",
    workspaceAgentStatusFailed: "失败",
    workspaceAgentStatusCanceled: "已取消",
    workspaceParticipantsTitle: "房间内的人类",
    workspaceParticipantsTopbarAria: "Workspace 成员",
    workspaceParticipantsInviteAria: "邀请成员",
    workspaceParticipantsInviteDisabled:
      "你不是这个房间的创建者，无法发起邀请哦",
    workspaceParticipantsInvitedCount_one: "已邀请 {{count}} 位成员",
    workspaceParticipantsInvitedCount_other: "已邀请 {{count}} 位成员",
    workspaceParticipantsOwnerBadge: "房主",
    workspaceParticipantsNoAgents: "暂未使用 Agent",
    workspaceParticipantsAgentCount_one: "使用过 {{count}} 种 Agent",
    workspaceParticipantsAgentCount_other: "使用过 {{count}} 种 Agent",
    workspaceParticipantsOnlineSummary_one: "{{count}} 人类和智能体在线",
    workspaceParticipantsOnlineSummary_other: "{{count}} 人类和智能体在线",
    workspaceParticipantsOverflowAria: "更多 Workspace 成员",
    workspaceXagentsCollab: "xagents 协同",
    workspaceThemeHint: "主题与外观",
    loadingTutti: "正在加载 Tutti…",
    runtimeArtifactStatus: {
      startupTitle: "正在准备 Workspace 运行环境",
      startupDescription:
        "Tutti 正在准备本机 Workspace 运行环境，完成后会进入登录页或首页。首次启动可能需要几分钟。",
      startupProgressHint: "正在检查本地运行环境文件并准备下载。",
      downloadedOfTotal: "{{downloaded}} / {{total}}",
      progressAria: "Workspace 运行环境下载进度",
      checking: "正在准备工作区环境…",
      downloading: "正在下载工作区环境…",
      downloadingPercent: "正在下载工作区环境… {{percent}}%",
      verifying: "正在完成环境准备…",
      error: "工作区环境准备失败",
      retry: "重试准备",
      retrying: "正在重试…",
      backgroundHint: "你可以先继续使用应用；进入工作区前会自动完成准备。",
      retryHint: "再次进入工作区时会自动重试。"
    },
    workspaceConnecting: "正在准备工作区…",
    workspaceEnterStatus: {
      enterStarted: "正在连接工作区…",
      prepareSandbox: "正在准备沙箱…",
      prepareToolchain: "正在准备工具链…",
      ensureRuntime: "正在启动工作区环境…",
      preAttachToolchain: "正在安装必需工具…",
      attachRuntime: "正在加载工作区…",
      applyProjections: "正在应用工作区文件…",
      applyConfigOverlays: "正在应用配置…",
      syncWorkspacePatch: "正在同步工作区状态…",
      enterSucceeded: "工作区已就绪",
      enterFailed: "进入工作区失败",
      resolvingCanvas: "正在恢复工作区画布…",
      finalizingWorkspace: "正在完成最后准备…"
    },
    roomRenameHint: "点击修改房间名称",
    roomRenameLabel: "房间名称",
    roomBack: "返回",
    roomExitActiveAgentTitle: "离开这个房间？",
    roomExitActiveAgentLead:
      "这个房间里仍有 Agent 在工作。离开会停止 Agent 进程，并将任务标记为已中断。",
    roomExitActiveAgentConfirm: "离开并中断",
    workspaceAgentSessionDetailToolCalls: "{{count}} 次工具调用",
    workspaceAgentSessionDetailThinking: "思考",
    workspaceAgentSessionDetailWorking: "工作中...",
    workspaceClosingBanner: "这个工作区正在关闭，连接即将断开。",
    workspaceClosingStatus: "工作区关闭中",
    workspaceAgentMessageExpand: "展开全部",
    workspaceAgentActivityStatusWorking: "运行中",
    workspaceAgentActivityStatusWaiting: "等待中",
    workspaceAgentActivityStatusIdle: "已完成",
    workspaceAgentActivityStatusEnd: "已完成",
    workspaceAgentActivityStatusCompleted: "已完成",
    workspaceAgentActivityStatusCanceled: "已取消",
    workspaceAgentActivityStatusFailed: "错误",
    workspaceAgentSessionDetailEmptyWithTimeline:
      "当前没有可展示的 session 消息。",
    workspaceAgentSessionDetailEmptyNoTimeline: "暂无 session 详情。",
    workspaceAgentSessionDetailOpenFile: "打开 {{path}}",
    shareJoin: {
      status: {
        checkingTitle: "正在检查邀请",
        checkingDescription: "正在校验邀请码并进入画布。",
        successTitle: "已加入房间",
        successDescription: "工作台列表已刷新，正在打开对应画布。",
        fullTitle: "房间人数已满",
        fullDescription: "这个 workspace 的协作者席位已经用完。",
        usedTitle: "邀请链接已失效",
        usedDescription: "这个邀请码已经被使用过。",
        revokedTitle: "邀请链接已失效",
        revokedDescription: "这个邀请码已经被 owner 作废。",
        invalidTitle: "无法加入房间",
        invalidDescription: "请确认链接是否完整，或让 owner 重新生成邀请。",
        authRequiredTitle: "需要登录",
        authRequiredDescription:
          "请先登录，登录后可以重新打开邀请链接继续加入。",
        defaultTitle: "无法加入房间",
        defaultDescription: "请确认链接是否完整，或让 owner 重新生成邀请。"
      },
      modal: {
        eyebrow: "Workspace Share",
        title: "协作者加入",
        recognizedRoom: "识别到房间",
        inviteCodePlaceholder: "输入邀请码",
        submit: "输入邀请码进入画布",
        submitting: "进入中…"
      }
    },
    datePicker: {
      placeholder: "年 / 月 / 日",
      displayValue: "{{year}} / {{month}} / {{day}}",
      monthLabel: "{{year}}年{{month}}月",
      previousMonth: "上个月",
      nextMonth: "下个月",
      clear: "清除",
      today: "今天",
      weekdaySun: "日",
      weekdayMon: "一",
      weekdayTue: "二",
      weekdayWed: "三",
      weekdayThu: "四",
      weekdayFri: "五",
      weekdaySat: "六"
    },
    collabResult: {
      title: "协作结果",
      actorFallback: "协作者",
      unread: "{{name}} 已标记任务完成，请查看最新结果。",
      read: "{{name}} 已标记任务完成。"
    },
    agentTool: {
      fallbackName: "使用工具",
      statusWorking: "进行中",
      statusCompleted: "已完成",
      statusFailed: "失败",
      statusCanceled: "已取消",
      statusWaiting: "等待中",
      details: {
        summary: "摘要",
        input: "输入",
        output: "输出",
        error: "错误",
        command: "命令",
        prompt: "任务提示",
        path: "路径",
        content: "内容",
        patch: "补丁",
        questions: "问题",
        steps: "步骤",
        query: "查询",
        scope: "范围",
        results: "结果",
        url: "链接",
        todos: "待办",
        skill: "技能",
        mcp: "MCP",
        mcpServer: "服务器",
        mcpTool: "工具",
        mcpItem: "条目 {{index}}",
        mcpDoc: "文档 {{index}}",
        approvalOptions: "审批选项",
        answerPrefix: "回答：{{answer}}",
        waitingForAnswer: "等待用户回答…",
        questionFallback: "问题",
        delegateSession: "委托会话",
        subAgents: "子智能体",
        subAgentStarting: "正在启动…",
        subAgentQueued: "排队中——等待可用执行槽…",
        subAgentFallbackName: "子智能体",
        subAgentEarlierOmitted: "已省略较早的 {{count}} 步",
        subAgentTask: "任务",
        subAgentProgress: "进展",
        missingFailureDetails: "Provider 报告失败，但没有返回失败详情。",
        noMatches: "没有匹配结果",
        stepLabel: "步骤 {{index}}",
        noMatchingTools: "没有匹配的工具",
        loadedAvailable: "已加载 {{loaded}} 个 · 共 {{available}} 个",
        contentTruncated: "内容已截断",
        summaryTruncated: "摘要已截断",
        rawPayload: "原始载荷",
        loadingDiff: "正在加载差异…",
        imagePreviewAlt: "图片生成预览",
        showFullContent: "展开完整内容（{{count}} 行）",
        collapseContent: "收起内容",
        showFullDiff: "展开完整差异（{{count}} 行）"
      },
      labels: {
        runCommand: "执行命令",
        readFile: "读取文件",
        writeFile: "写入文件",
        editFile: "编辑文件",
        listFiles: "查看文件",
        searchFiles: "搜索文件",
        webSearch: "搜索网页",
        webFetch: "读取网页",
        applyPatch: "应用修改",
        useTool: "使用工具",
        findFiles: "查找文件",
        readCommandOutput: "读取命令输出",
        stopCommand: "停止命令",
        readNotebook: "读取 Notebook",
        editNotebook: "编辑 Notebook",
        updateTodos: "更新待办",
        delegateAgent: "委托 Agent",
        closeAgent: "结束 Agent",
        waitAgent: "等待 Agent",
        currentIssue: "当前任务",
        thinking: "思考",
        responding: "回复",
        notification: "通知"
      }
    },
    workspaceAgentProbeAvailableAria: "可用",
    workspaceAgentProbeUnavailableAria: "不可用",
    workspaceAgentProbeUnknownAria: "状态未知",
    workspaceAgentProbeDockChecking: "正在检测可用性…",
    workspaceAgentProbeDockNoData: "暂无该房间的探测数据",
    workspaceAgentProbeDockAvailable: "可用",
    workspaceAgentProbeDetailStatus: "状态",
    workspaceAgentProbeDetailQuota: "额度",
    workspaceAgentProbeUsageUnsupported: "暂未接入用量",
    workspaceAgentProbeQuotaResetTimeLabel: "{{label}}重置时间",
    ...zhCNWorkspaceAgentProbeQuota,
    workspaceAgentProbeLoadingUsage: "加载用量中...",
    workspaceAgentProbeScrollListRight: "向右查看更多 Agent",
    workspaceAgentProbeScrollListLeft: "向左查看前面的 Agent",
    workspaceAgentProbeAgentUsage: "用量",
    workspaceAgentProbeQuotaSession: "会话",
    workspaceAgentProbeQuotaWeekly: "每周",
    workspaceAgentProbeQuotaMonthly: "每月",
    workspaceAgentProbeQuotaDaily: "每日",
    workspaceAgentProbeQuotaCost: "费用",
    workspaceAgentProbeErrorAuthRequired: "需要登录",
    workspaceAgentProbeErrorSessionExpired: "会话已过期",
    workspaceAgentProbeErrorSubscriptionRequired: "需要订阅",
    workspaceAgentProbeErrorParseFailed: "无法解析用量输出",
    workspaceAgentProbeErrorNoData: "暂无用量数据",
    workspaceAgentProbeErrorTimeout: "用量探测超时",
    workspaceAgentProbeErrorUnavailable: "用量探测失败",
    roomParticipantsTitle: "房间内的人类",
    roomParticipantsTopbarAria: "Room 成员",
    roomParticipantsInviteAria: "邀请成员",
    roomParticipantsInviteDisabled: "你不是这个房间的创建者，无法发起邀请哦",
    roomParticipantsInviteOwnerOnlyTooltip: "仅支持房主发起邀请",
    roomParticipantsLeaveRoom: "退出房间",
    roomParticipantsInvitedCount_one: "已邀请 {{count}} 位成员",
    roomParticipantsInvitedCount_other: "已邀请 {{count}} 位成员",
    roomParticipantsOwnerBadge: "房主",
    roomParticipantsNoAgents: "暂未使用 Agent",
    roomParticipantsAgentCount_one: "使用过 {{count}} 种 Agent",
    roomParticipantsAgentCount_other: "使用过 {{count}} 种 Agent",
    roomParticipantsOnlineSummary_one: "{{count}} 人类和智能体在线",
    roomParticipantsOnlineSummary_other: "{{count}} 人类和智能体在线",
    roomParticipantsOverflowAria: "更多 Room 成员",
    roomConnecting: "正在准备房间…",
    roomCanvasLoadErrorTitle: "当前房间画布数据加载异常",
    roomCanvasLoadErrorDescription:
      "未能加载当前房间的画布数据。请刷新后重试。",
    roomCanvasLoadRetry: "刷新",
    roomCanvasLoadRetrying: "刷新中…",
    roomRecoveryRenderFailed: "后端已恢复房间，但未能渲染工作区。请重新加载。",
    runtimeConnectionLostTitle: "运行时连接异常",
    runtimeConnectionLostSummary: "运行时连接未能快速恢复。请重启应用后继续。",
    runtimeConnectionLostLead: "应用会重新启动，并拉起一个新的本机运行时。",
    runtimeConnectionRefresh: "重启应用",
    runtimeConnectionRefreshing: "重启中…",
    runtimeConnectionRefreshSucceeded: "已请求重启应用。",
    roomEntryDiscarded: "未能完成进入房间（可能被新的操作打断），请再试一次。",
    roomEnterStatus: {
      enterStarted: "正在连接房间…",
      prepareSandbox: "正在准备沙箱…",
      prepareToolchain: "正在准备工具链…",
      ensureRuntime: "正在启动工作区环境…",
      runTemplateHook: "正在执行应用初始化…",
      preAttachToolchain: "正在安装必需工具…",
      attachRuntime: "正在加载工作区…",
      applyProjections: "正在应用房间文件…",
      applyConfigOverlays: "正在应用配置…",
      syncWorkspacePatch: "正在同步房间状态…",
      enterSucceeded: "房间已就绪",
      enterFailed: "进入房间失败",
      resolvingCanvas: "正在恢复房间画布…",
      finalizingWorkspace: "正在完成最后准备…"
    },
    simpleMode: {
      pageHeadline: "和伙伴、Agent 一起，在同一个房间里协作",
      fusedButton: {
        main: "创建协作房间",
        hint: "拖入文件或文件夹作为起点，伙伴进入后可以一起操作",
        hintActive: "松开即可用这些内容创建协作房间"
      },
      note: {
        label: "备注",
        placeholder: "这个房间是做什么的？（选填）"
      },
      overlayStatus: {
        creating: "正在准备你们的房间…",
        generatingInvite: "房间准备好了，正在生成邀请链接…",
        done: "即将进入房间…",
        error: "刚刚出了点问题"
      },
      overlayActions: {
        retry: "重试",
        dismiss: "回到上一步"
      },
      inviteModal: {
        title: "把这条链接发给你的伙伴",
        body: "对方点击链接就能进入同一个房间，你们可以在这里共同操作刚刚拖进来的内容。",
        linkLabel: "邀请链接",
        copy: "复制",
        copied: "已复制",
        primary: "完成",
        secondary: "稍后再分享"
      }
    },
    unknownProvider: "未知提供方",
    messages: {
      roomOpened: "已打开房间",
      roomOpenedIssue: "已打开房间，准备定位到对应任务",
      issueTitleRequired: "请先填写任务标题",
      issueCreated: "任务已创建",
      issueUpdated: "任务已更新",
      issueShareLinkCopied: "任务分享链接已复制",
      issueShareFallbackRoomFull:
        "当前房间已满，已复制仅房间成员可打开的任务链接",
      issueShareFallbackInviteFull:
        "邀请链接名额已满，已复制仅房间成员可打开的任务链接",
      issueWorkspaceNotReady: "当前 Room Workspace 尚未就绪，请稍后重试",
      issueRunSessionUnavailable: "该次执行未关联可回放 session",
      issueDetailRequiredBeforeRun: "请先打开对应任务详情后再执行",
      issueNoAvailableAgent: "当前没有可用的 Agent",
      issueRunStarted: "已发送到 {{provider}}，可调整后执行",
      issueStatusUpdated: "任务状态已更新",
      issueDeleteForbidden: "仅任务创建者可以删除",
      issueNotFoundOrDeleted: "任务不存在或已被删除",
      issueDeleted: "任务已删除",
      issueContextAdded: "已补充引用资料",
      issueContextRemoved: "已移除引用资料",
      issuePendingUploadAdded: "已加入 {{count}} 项待上传资料",
      issueUploaded: "已上传 {{count}} 项资料",
      issueDirectInstallUnsupported: "当前不支持在这里直接安装 Agent",
      collabCompleted: "该协作任务已完成",
      githubLoginCompleted: "GitHub 登录完成",
      googleLoginCompleted: "Google 登录完成"
    },
    issue: {
      sidebarEyebrow: "任务中心",
      sidebarTitle: "任务列表",
      sidebarHint: "房间成员都能查看任务，快速定位到协作任务。",
      sidebarResizeAria: "调整任务列表宽度",
      create: "新建任务",
      loading: "正在加载任务...",
      empty: "还没有任务，先创建一条来开始协作。",
      metaPriority: "优先级 {{value}}",
      metaDue: "截止 {{value}}",
      stageEyebrow: "任务中心全流程",
      stageTitle: "以任务为中心，把上下文、执行与产物串成一条完整链路。",
      stageDescription:
        "房间里先建立任务对象，再引用 Files 补充资料，最后由协作者手动使用自己的 Agent 发起执行。",
      shareThisIssue: "分享这个任务",
      flowList: "查看任务",
      flowCreate: "新建任务",
      flowContext: "引用 Files",
      flowDetail: "查看详情",
      flowRun: "决定执行方式",
      flowOutput: "查看产物",
      currentFocusTitle: "当前 Focus",
      currentFocusSubtle:
        "当前选中的任务会在这里浓缩展示，帮助你快速进入下一步。",
      currentFocusEmpty: "先从左侧选择任务，或创建一个新的任务来开启执行流程。",
      defaultDescription:
        "补充背景、现象与期望结果，让这个任务更容易被协作者接手。",
      metricReferences: "引用资料",
      metricRunHistory: "执行历史",
      metricOutputs: "最新产物",
      countFiles: "{{count}} 个",
      countRuns: "{{count}} 次",
      executionModeTitle: "执行方式",
      executionModeSubtle:
        "V1 统一采用“分享链接进入任务，再手动用自己的 Agent 执行”。",
      executionMyAgentTag: "我的 Agent",
      executionMyAgentDescription:
        "在详情页直接发起一次 Run，执行结果回写到 Files。",
      executionShareTag: "分享链接",
      executionShareDescription:
        "把协作者带到同一个任务，再由对方手动点击执行，避免 V1 做自动代跑。",
      outputsTitle: "执行产物",
      outputsSubtle: "执行完成后，摘要、最近 Run 与 Files 会形成闭环。",
      outputsEmpty: "暂无执行产物",
      fileFallback: "file",
      detailEyebrow: "任务详情",
      collapse: "收起",
      detailLoading: "正在加载详情…",
      overviewTitle: "概览",
      overviewSubtle: "创建后进入详情页，补充资料并决定由谁执行。",
      runWithMyAgent: "用我的 Agent 执行",
      rerunWithMyAgent: "再次用我的 Agent 执行",
      summaryPriority: "优先级",
      summaryDueDate: "截止时间",
      summaryReferences: "引用资料",
      summaryRunHistory: "执行历史",
      noDescription: "暂无描述",
      shareTipCanInvite: "可分享给房间内外协作者，首次打开将自动进入该任务。",
      shareTipRoomFull: "当前房间已满，仅房间内成员可通过该链接打开此任务。",
      referencesTitle: "引用资料",
      referencesSubtle: "从 Room Workspace 中选择上下文文件，添加为任务资料。",
      addReferences: "引用 Files",
      referencesEmpty: "暂无引用资料，建议先补充执行上下文。",
      runResultTitle: "执行结果",
      prepareRunTitle: "准备执行",
      runResultSubtle: "查看最新执行结果、产物与历史 Run，并支持再次发起执行。",
      prepareRunSubtle: "创建后进入详情页，完成资料补充，再决定执行方式。",
      runHeroTitle: "我的 Agent · Run {{runId}}",
      runSummaryEmpty: "暂无执行结果",
      runStatusRunning: "执行中，正在等待最新进展",
      runStatusCompleted: "执行已结束，暂未返回摘要",
      runStatusFailed: "执行失败，暂未返回错误摘要",
      runDetailsLoading: "正在加载该次 Run 的执行明细…",
      runOutputsEmpty: "产物稍后会出现在 Files 中。",
      prerunEmpty:
        "还没有执行记录。建议先补充引用资料，再点击“用我的 Agent 执行”。",
      shareWithCollaborator: "分享链接给协作者",
      historyTitle: "执行历史",
      historyHint: "点击切换到对应 Run 结果",
      currentRunOutputsTitle: "本次 Run 产物",
      currentRunOutputsSubtle: "执行产物会写回 Room，可预览并继续复用。",
      currentRunOutputsEmpty: "当前 Run 暂无产物文件。",
      directoryViewTitle: "目录视图",
      directoryViewSubtle: "如果 Run 产出一个目录，这里会联动展示目录下文件。",
      directoryViewEmpty: "还没有可联动展示的产物目录。",
      directoryKind: "DIR",
      fileKind: "FILE",
      createTitle: "新建任务",
      createSubtle: "填写需求与描述，让任务进入可执行状态。",
      fieldTitle: "标题",
      titlePlaceholder: "请输入任务标题",
      fieldDescription: "需求描述",
      descriptionPlaceholder: "补充背景、现象、影响范围与期望结果",
      descriptionPlaceholderLegacy: "描述任务背景、目标和期望结果。",
      fieldPriority: "优先级",
      priorityOptionMedium: "中",
      priorityOptionHigh: "高",
      priorityOptionLow: "低",
      fieldDueDate: "截止时间",
      attachmentsOptional: "资料（可选）",
      attachmentsEmpty: "还没有加入任何资料",
      createReferencesTitle: "引用 Files",
      createReferencesSubtle:
        "从房间 Workspace 中选择上下文文件，沉淀为任务资料。",
      selectReferences: "选择引用",
      selectionNone: "暂未选择文件",
      selectionCount: "已选择 {{count}} 个文件",
      selectionEmpty: "暂未选择资料。",
      saveIssue: "保存任务",
      contextPickerCreateTitle: "引用 Files",
      contextPickerDetailTitle: "补充引用资料",
      contextPickerSubtle: "从 Room Workspace 选择执行上下文文件。",
      contextPickerEmpty: "还没有选中的文件。",
      completeReferences: "完成引用",
      addToIssue: "添加到任务",
      addingToIssue: "添加中...",
      dueDateUnset: "未设置",
      runTimeJustNow: "刚刚",
      createdJustNow: "刚刚创建",
      searchPlaceholder: "搜索任务",
      sidebarEmpty: "这里将呈现房间的任务",
      sidebarCategoryEmpty: "此分类下暂无任务",
      searchEmptyTitle: "没有匹配的任务",
      searchEmptyDescription: "换个关键词再试试。",
      defaultEmptyTitle: "还没有任务",
      defaultEmptyDescription:
        "新建任务，补充需求描述和相关文件，即可委托他人或自己的 Agent 执行任务",
      defaultSelectTitle: "选择一个任务",
      defaultSelectDescription:
        "从左侧列表进入详情，或者新建一个新的协作任务。",
      editTitle: "编辑任务",
      uploadFile: "上传文件",
      uploadFolder: "上传文件夹",
      referenceWorkspaceFiles: "引用空间文件",
      pendingUploadArchiveHint: "创建任务后自动归档到资料目录",
      latestExecutionTitle: "最新执行状态",
      executionRecordsTitle: "执行记录",
      viewDetails: "查看详情",
      copyLink: "复制链接",
      copyingLink: "复制中...",
      inviteCollaborator: "邀请协作",
      shareWithCollaboratorHint: "发给协作者，对方可用 Agent 帮你执行",
      summaryTitle: "需求摘要",
      outputsTitleLegacy: "产物",
      filePickerTitle: "选择要加入任务的资料",
      searchRoomFilesPlaceholder: "搜索 Files...",
      noReferenceFiles: "暂无可引用文件",
      selectReferenceRequired: "必须选择一个进行引用",
      expandItem: "展开 {{label}}",
      collapseItem: "收起 {{label}}",
      toggleSelection: "切换选择 {{label}}",
      selectionCountShort: "已选择 {{count}} 项",
      currentPreview: "当前预览",
      previewPrompt: "选择左侧资料即可预览内容",
      previewPromptCompact: "选择上方资料即可预览内容",
      filePickerDirectoryPreview: "选择左侧其他文件即可切换预览",
      filePickerDirectoryPreviewCompact: "选择上方其他文件即可切换预览",
      contextEmpty: "暂未引用资料",
      openReference: "打开引用资料 {{label}}",
      outputsEmptyCompact: "暂无产物",
      openOutput: "打开产物 {{label}}",
      scrollFiltersLeft: "向左滚动任务分类",
      scrollFiltersRight: "向右滚动任务分类",
      edit: "编辑",
      runStarting: "发起中...",
      runtimeUnavailable: "当前 runtime 不可用",
      unavailable: "不可用",
      install: "安装",
      installing: "安装中…",
      sync: "同步",
      syncing: "同步中…",
      filterAll: "全部",
      statusAriaLabel: "任务状态",
      priorityAriaLabel: "任务优先级",
      metaCreator: "创建者 {{value}}",
      metaCreatedAt: "创建时间 {{value}}",
      metaDueAt: "截止时间 {{value}}",
      metaCompletedAt: "完成时间 {{value}}",
      requesterFallback: "用户",
      agentCodex: "Codex",
      agentClaudeCode: "Claude Code",
      agentTutti: "Tutti",
      agentHermes: "Hermes",
      agentOpenClaw: "OpenClaw",
      statusNotStarted: "待执行",
      statusRunning: "执行中",
      statusPendingAcceptance: "待验收",
      statusCompleted: "已完成",
      statusFailed: "执行失败",
      statusCanceled: "已取消",
      priorityHigh: "高优",
      priorityMedium: "中等",
      priorityLow: "低优"
    },
    roomIssueNode: {
      statusTabAll: "全部",
      statusTabNotStarted: "未启动",
      statusTabRunning: "执行中",
      statusTabPendingAcceptance: "待验收",
      statusTabCompleted: "已完成",
      emptyContent: "暂无内容",
      taskCount_one: "{{count}} 个子任务",
      taskCount_other: "{{count}} 个子任务",
      taskCountEmpty: "暂无子任务",
      taskRunningCount: "{{count}} 个执行中",
      taskPendingAcceptanceCount: "{{count}} 个待验收",
      taskCompletedCount: "{{count}} 个已完成",
      issueStatusNotStarted: "待开始",
      issueStatusRunning: "执行中",
      issueStatusInProgress: "执行中",
      issueStatusPendingAcceptance: "待验收",
      issueStatusCompleted: "已完成",
      issueStatusFailed: "失败",
      issueStatusCanceled: "已取消",
      issueStatusUnknown: "未知状态",
      runStatusRunning: "执行中",
      runStatusCompleted: "已完成",
      runStatusFailed: "失败",
      runStatusCanceled: "已取消",
      runStatusIdle: "未开始",
      issueTitleRequired: "请输入任务标题",
      taskTitleRequired: "请输入任务标题",
      selectTaskFirst: "请先选择一个任务",
      deleteTaskDialogTitle: "删除任务",
      deleteTaskConfirm: "确认删除任务「{{title}}」吗？",
      deleteIssueDialogTitle: "删除子任务",
      deleteIssueConfirm: "确认删除子任务「{{title}}」吗？",
      issueTaskUnavailable: "当前任务缺少所属任务，暂时无法继续操作",
      shareCopied: "邀请链接已复制",
      outputOpenUnavailable: "当前没有可打开的产物",
      sessionUnavailable: "当前没有可打开的会话",
      editTask: "编辑任务",
      createTask: "新建任务",
      taskEditorLead: "补充任务标题和描述，方便继续推进执行。",
      taskTitlePlaceholder: "请输入任务标题",
      taskContentPlaceholder: "补充任务背景、目标和验收预期",
      saveTask: "保存任务",
      cancel: "取消",
      noTaskTitle: "还没有任务",
      noTaskDescription:
        "新建任务，补充需求描述和相关文件，即可委托他人或自己的 Agent 执行任务",
      favoriteTask: "收藏任务",
      creator: "创建者",
      createdAt: "创建时间",
      edit: "编辑",
      delete: "删除",
      description: "描述",
      emptyTaskContent: "暂未补充任务描述",
      emptyIssueContent: "暂未补充子任务描述",
      references: "引用文件",
      emptyReferences: "暂未引用文件",
      editIssue: "编辑子任务",
      createIssue: "新增子任务",
      issueTitlePlaceholder: "请输入子任务标题",
      issueContentPlaceholder: "补充子任务目标、执行方式和验收标准",
      saveIssue: "保存子任务",
      issueSelectionHint: "选择一个任务后，这里会展示任务详情与 Agent 执行状态",
      timeLabel: "时间 {{value}}",
      updatedAtLabel: "更新时间",
      runNotStarted: "暂无执行记录",
      issueDetailsLoading: "正在加载任务详情…",
      agentExecution: "Agent 执行",
      noIssuesTitle: "还没有任务",
      noIssuesTaskShellDescription: "你可以围绕当前任务继续添加子任务",
      taskExecutionTitle: "任务执行中",
      taskExecutionDescription:
        "当前进展会显示在这里，需要补充新的方向时，可继续新增子任务",
      deleteIssue: "删除子任务",
      searchPlaceholder: "搜索任务",
      refreshTasksAria: "刷新任务列表",
      collapseTaskList: "收起任务列表",
      expandTaskList: "展开任务列表",
      noTasksForFilterTitle: "当前筛选条件下暂无任务",
      noTasksForFilterBody: "试试切换筛选条件，或者新建一个任务",
      loadMore: "加载更多",
      issuesSection: "子任务",
      createSubtask: "添加",
      createSubtaskTitle: "添加子任务",
      editSubtask: "编辑子任务",
      saveSubtask: "保存子任务",
      issueTableTitle: "标题",
      issueTableDescription: "描述",
      issueTableStatus: "状态",
      issueTableOwner: "执行者",
      issueTableTime: "时间",
      issueTableActions: "操作",
      unassigned: "待分配",
      view: "查看",
      share: "分享",
      run: "启动",
      inviteCollaborator: "邀请协作",
      askAgentToRun: "发送给 Agent",
      moreActions: "更多操作",
      closeIssueDrawerAria: "关闭任务详情抽屉",
      runWithProvider: "用 {{provider}} 执行",
      noIssues: "当前任务下暂无子任务。",
      noManualIssues: "新增子任务后，这里会展示后续跟进内容。",
      currentMember: "我",
      closeWindowAria: "关闭 {{title}}"
    },
    workspaceSidebarRuntimeTitle: "运行时",
    workspaceSidebarRuntimeDescription: "当前画布的实时上下文。",
    workspaceSidebarLiveBadge: "在线",
    workspaceSidebarRootPrefix: "根目录：",
    workspaceSidebarSandboxPrefix: "沙箱：",
    workspaceSidebarSessionsPrefix: "会话：",
    workspaceSidebarProviderPrefix: "提供方：",
    workspaceSidebarTreeTitle: "目录树",
    workspaceSidebarTreeDescription: "运行时可见的房间结构。",
    workspaceSidebarTreeKindDir: "目录",
    workspaceSidebarTreeKindFile: "文件",
    workspaceSidebarProfileMe: "我",
    profile: {
      openMenu: "打开用户菜单",
      accountSettings: "用户信息",
      accountSettingsTitle: "用户信息",
      accountSettingsLead: "更新你在各房间中显示的用户名和头像。",
      accountSettingsNameLabel: "用户名",
      accountSettingsNamePlaceholder: "输入用户名",
      accountSettingsAvatarLabel: "头像",
      accountSettingsUpload: "上传图片",
      accountSettingsDefaultAvatars: "默认头像",
      accountSettingsInvalidImage: "请选择有效的图片文件。",
      accountSettingsImageTooLarge: "图片大小不能超过 2 MB。",
      accountSettingsCropTitle: "裁剪头像",
      accountSettingsCropZoom: "缩放",
      accountSettingsCropRotation: "旋转",
      accountSettingsCropCancel: "取消裁剪",
      accountSettingsCropApply: "使用图片",
      accountSettingsSaved: "账户设置已保存。",
      themeSystem: "跟随系统",
      themeLight: "浅色",
      themeDark: "深色",
      language: "语言",
      logout: "退出登录",
      logoutDone: "退出登陆成功",
      labTitle: "实验室",
      labDirectVMTerminalTitle: "VM 终端",
      labDirectVMTerminalDescription:
        "在启动页显示一个入口，直接打开托管 Linux VM 内的 shell",
      labDebugTerminalTitle: "调试终端",
      labDebugTerminalDescription: "在房间 dock 中显示绑定工作区的调试终端"
    },
    launch: {
      navLaunch: "开始",
      navTemplates: "场景模板",
      navManageAgents: "管理智能体",
      navConnectors: "连接器",
      navVMTerminal: "VM 终端",
      vmTerminalStarting: "正在启动 VM...",
      vmTerminalRetry: "重试",
      vmTerminalSessionLost: "VM 终端会话已断开。",
      vmTerminalProcessExited: "[进程已退出，退出码 {{exitCode}}]",
      manageAgentsTitle: "管理智能体",
      dock: {
        agentNotInstalled: "未检测到本机配置，请完成本地安装后再同步。",
        agentNotSynced: "检测到本机配置，是否立即同步？"
      },
      manageAgentsGuideLead:
        "同步或移除只影响 Tutti，不会改动你电脑上的智能体。",
      manageAgentsColumnAgent: "智能体",
      manageAgentsColumnRun: "运行在",
      manageAgentsColumnInstallStatus: "同步状态",
      manageAgentsColumnSettings: "配置",
      manageAgentActionInstall: "同步",
      manageAgentActionSync: "同步",
      manageAgentActionSyncTooltip: "将自动同步你的本机配置",
      manageAgentRuntimeArtifactPreparingTooltip:
        "工作区环境仍在准备中，完成后即可同步。",
      manageAgentActionUninstall: "移除",
      manageAgentActionInstalling: "正在同步…",
      manageAgentActionSyncing: "正在同步…",
      manageAgentActionRetry: "重试",
      manageAgentActionUninstalling: "正在移除…",
      manageAgentActionUninstallWaiting: "等待移除…",
      manageAgentUninstallConfirm:
        "只会从 Tutti 中移除 {{name}}，不会影响 Tutti 以外的应用、登录状态或配置。",
      manageAgentCellRunsLocal: "Tutti 本地",
      manageAgentCellInstalled: "已同步",
      manageAgentCellNotInstalled: "未同步",
      manageAgentUninstallWaiting: "等待移除",
      manageAgentUninstallRemoving: "正在移除",
      manageAgentDownloadDownloading: "正在下载 {{agent}} · {{percent}}",
      manageAgentDownloadRetrying: "正在重试下载 · {{attempt}}/{{max}}",
      manageAgentDownloadFailed: "下载失败",
      manageAgentDownloadWaitingInstall: "等待安装",
      manageAgentDownloadInstalling: "正在安装",
      manageAgentConfigWillUse: "检测到本机配置",
      manageAgentConfigWillSyncFrom:
        "同步后会沿用 {{device}} 上的 {{agent}} 配置",
      manageAgentConfigSynced: "已同步 · {{time}}",
      manageAgentConfigSyncedNoTime: "已同步",
      manageAgentConfigSyncedFrom: "已从 {{device}} 同步到 Tutti · {{time}}",
      manageAgentConfigSyncedFromNoTime: "已从 {{device}} 同步到 Tutti",
      manageAgentConfigNone: "未发现配置",
      manageAgentConfigNotDetected: "未检测到本机配置",
      manageAgentMissingHostConfigTooltip:
        "{{agent}} 依赖你的本地安装，请先完成本地安装再进行同步",
      manageAgentTuttiDefaultConfig: "Tutti 默认配置",
      manageAgentHostDeviceFallback: "这台电脑",
      manageAgentSyncTimeJustNow: "刚刚",
      manageAgentSyncTimeMinutesAgo: "{{count}} 分钟前",
      manageAgentSyncTimeHoursAgo: "{{count}} 小时前",
      manageAgentSyncTimeDaysAgo: "{{count}} 天前",
      manageAgentsOpenclawHint:
        "若要在房间内使用 OpenClaw，请前往管理智能体同步 OpenClaw。",
      kicker: "Tutti · 桌面工作台",
      promoBadge: "你目前有 {{count}} 个可用 agents，可以在房间内使用它们。",
      promoBadgeNavigateAria:
        "前往管理智能体，你目前有 {{count}} 个可用 agents",
      heroTitle: "首个多人与多 agent 实时协作 OS",
      updateAction: "更新",
      updateInstallingAction: "重启更新",
      updateDownloadingAction: "更新中 {{percent}}",
      updateCardAvailableTitle: "版本 {{version}} 可用",
      updateCardAvailableDetail: "准备好时再开始下载，不会打断当前操作。",
      updateCardDownloadingTitle: "正在下载 {{version}}",
      updateCardDownloadingDetail: "更新包正在后台下载。",
      updateCardDownloadedTitle: "更新已准备就绪",
      updateCardDownloadedDetail: "重启 Tutti 后即可完成 {{version}} 的安装。",
      updateCardDownloadAction: "下载更新",
      updateCardInstallAction: "重启并安装",
      sectionSpacesTitle: "房间",
      sectionAgentsTitle: "Agents",
      sectionAgentsAction: "管理",
      sectionAgentsEmpty: "你暂无任何可用 Agents",
      sectionAgentsInstallAction: "立即装载",
      viewAllRooms: "查看全部",
      roomsExpand: "展开更多 ({{count}})",
      roomsCollapse: "收起",
      roomsLoadMore: "加载更多",
      roomsEmptyPlaceholder: "暂未创建或加入房间",
      recommendedTemplatesTitle: "场景模板",
      recommendedTemplatesViewMore: "更多",
      topRailOpenCreateAria: "创建新房间，在弹窗中输入房间名称",
      topRailOpenJoinAria: "加入已有房间，在弹窗中粘贴分享链接",
      roomCardStatusActive: "进行中",
      roomCardStatusIdle: "空闲",
      roomCardStatusFailed: "失败",
      roomCardPreviewActorUser: "用户",
      roomCardPreviewPlaceholder: "房间内暂无最新状态",
      roomCardSnapshotAria: "「{{name}}」最近可视状态",
      roomCardHumansCount_one: "{{count}} 人",
      roomCardHumansCount_other: "{{count}} 人",
      roomCardAgentsCount_one: "{{count}} 个 Agent",
      roomCardAgentsCount_other: "{{count}} 个 Agent",
      roomCardOnline: "在线",
      roomCardAgentFallback: "Agent",
      roomCardAgentsAria: "「{{name}}」中 {{count}} 个活跃 Agent",
      roomCardOpenRoomAria: "进入房间 {{name}}",
      roomCardMoreActions: "房间操作菜单",
      roomCardRenameWorkspace: "重命名",
      roomRenameDialogTitle: "重命名房间",
      roomRenameDialogLead: "请输入该房间的新显示名称。",
      roomRenamePlaceholder: "房间名称",
      roomCardAvatarsAria: "「{{name}}」参与者头像",
      roomsDirectoryLead: "浏览并进入你参与过的所有房间。",
      roomsDirectoryEmpty: "暂无房间记录。你可以在「开始」页创建或加入房间。",
      sectionTemplatesTitle: "agent竞技场",
      sectionOfficeSpaceTitle: "办公",
      officeScenario1Title: "OPC一人团队",
      officeScenario1Description: "一人掌控多个 Agent，高效完成复杂任务",
      officeScenario2Title: "协同代办",
      officeScenario2Description:
        "将任务发给朋友电脑里的 agent，协同完成报销、整理等事务，可通过物理终端直连，安全性高",
      taskCenterTitle: "协同代办",
      taskCenterDescription:
        "新建一个协作房间，马上开始整理任务、分派成员和跟进进度。",
      officeScenario3Title: "组织协作",
      officeScenario3Description: "多人多 Agent 实时协同，过程与成果全局共享",
      sectionCollaborateSpaceTitle: "开发与交付",
      sectionContentCreationSpaceTitle: "内容创作",
      sectionMore: "更多",
      sectionMoreAriaComingSoon: "更多，即将推出",
      sectionMoreComingSoonHint: "即将推出",
      betaGateCheckingTitle: "正在检查内测权限",
      betaGateCheckingBody: "我们正在确认当前账号是否已经拥有 Tutti 内测资格",
      betaGateBlockedTitle: "当前账号暂未获得内测资格",
      betaGateBlockedBody: "输入邀请码以解锁 Tutti 桌面端内测",
      betaGateInviteCodeLabel: "邀请码",
      betaGateInviteCodePlaceholder: "输入邀请码",
      betaGateInviteCodeRequired: "请先输入邀请码。",
      betaGateRetry: "重新检测",
      betaGateSubmit: "验证邀请码",
      betaGateBackToLogin: "退出登录",
      betaGateSubmitting: "验证中…",
      betaGateSubmitSuccess: "邀请码已通过",
      betaGateSubmitFailed: "验证邀请码失败。",
      betaGateLoadFailed: "加载内测状态失败。",
      scenarioCloneUseHint: "克隆并使用场景模板",
      contentScenario1Title: "产品原型",
      contentScenario1Description:
        "产品经理、设计师与智能Agent同步工作，快速生成高度匹配需求的原型方案。",
      contentScenario2Title: "PPT制作",
      contentScenario2Description:
        "多个智能Agent协作，从内容框架、设计到排版，美化一气呵成。",
      contentScenario3Title: "视频创作",
      contentScenario3Description:
        "不同Agent协同操作，实时生成脚本、分镜与剪辑，极速产出高质量视频。",
      templatesLoading: "正在加载场景模板…",
      templatesLoadFailed: "场景模板加载失败。",
      templatesRetry: "重试",
      templatesEmpty: "当前暂无可用场景模板。",
      templatePlayCta: "玩一玩",
      collaborateSlotHoverCta: "即将推出",
      scenarioBadgeCollaborate: "协同",
      scenarioBadgeGame: "游戏",
      templateVersion: "版本 {{version}}",
      templatePlaceholderTitle: "场景模板",
      templatePlaceholderHint: "即将接入",
      templateGomokuTitle: "五子棋对局",
      templateGomokuDescription:
        "AI协助决策，优化每一步棋局布局，挑战极限智力对抗。",
      templateTexasHoldemTitle: "德州扑克博弈",
      templateTexasHoldemDescription:
        "实时分析和调整策略，比拼你的agent智能程度。",
      templateChineseChessTitle: "象棋对弈",
      templateChineseChessDescription:
        "AI深度策略引导，模拟真人对弈，享受人机对抗的极致体验。",
      collaborateDemoBuildTitle: "日常开发",
      collaborateDemoBuildDescription:
        "为团队全体成员提供统一的协同开发环境，任务与上下文自然流转",
      collaborateDevAcceptanceTitle: "自动化测试验收",
      collaborateDevAcceptanceDescription:
        "自动搭建测试环境，让房间快速执行测试和验收",
      collaborateUnifiedWorkbenchTitle: "一站式工作台",
      collaborateUnifiedWorkbenchDescription:
        "集成各类应用与 Agent，统一编排与执行任务",
      intentCreateTitle: "创建新房间",
      intentCreateBody: "邀请伙伴及其 AI 智能体一起协作",
      intentLocalDirTitle: "加入已有房间",
      intentLocalDirBody: "粘贴分享链接即可进入房间",
      intentCreateSecondaryCta: "创建",
      intentJoinSecondaryCta: "通过链接加入",
      agentToolNameOpenclaw: "OpenClaw",
      agentToolNameClaudeCodeRouter: "Claude Code Router",
      openclawBlockTitle: "需要 OpenClaw？",
      openclawBlockBody:
        "可一键开启：保存后，在「开始」里「创建并进入」新房间时会按官方流程安装并接入；若已装过，也会标记为之后新房间默认使用。",
      openclawOneClickCta: "一键启用 OpenClaw",
      openclawEnabledHint: "已启用，新房间将默认接入",
      openclawOneClickSaveDone: "已启用 OpenClaw 并保存",
      headline: "从一块房间画布开始",
      lead: "为运行时、终端与 Agent 建立独立分区。命名并创建房间即可进入画布；也可以连接已有环境，或从最近记录一键继续。",
      step1: "创建房间，获得隔离的运行时与沙箱目录。",
      step2: "在画布上编排终端、任务与文档，用分区区隔不同工作流。",
      step3: "进入画布，在房间内运行终端与 Agent。",
      cardTitle: "创建房间",
      cardHint: "使用能区分任务或迭代的名称，便于在历史与协作中识别。",
      namePlaceholder: "例如：acme-web-refactor",
      creating: "创建中…",
      createCta: "创建并进入画布",
      recentTitle: "最近使用",
      joinWorkspaceCancel: "取消",
      joinLinkPlaceholder: "粘贴分享链接",
      joinLinkSubmit: "立即进入",
      joinLinkParseError: "无法识别该内容，请粘贴有效链接",
      joinInviteCodeNextHint: "已识别房间，请输入邀请码加入",
      runtimeEntryNamePlaceholder: "输入房间名称",
      runtimeEntryCreating: "处理中…",
      intentEntryModalConfirm: "确认",
      runtimeEntryCheckingBetaAccess: "检测内测资格中",
      runtimeEntryCreateSubmit: "立即开始",
      sidebarWorkspace: "我的房间",
      sidebarRecents: "最近",
      sidebarNoRecents: "暂无最近房间。",
      ownedWorkspaces: "我的房间",
      sharedWorkspaces: "与我共享",
      recentWorkspaces: "最近房间",
      unnamedWorkspace: "未命名房间",
      workspaceRoleOwner: "管理者",
      workspaceRoleCollaborator: "协作者",
      sidebarSearchPlaceholder: "搜索…",
      sidebarNewWorkspace: "新建房间",
      resizeSidebar: "调整侧边栏宽度",
      sidebarDeleteWorkspace: "删除房间",
      sidebarDeleteWorkspaceConfirmTitle: "删除这个房间？",
      sidebarDeleteWorkspaceConfirm:
        "“{{name}}” 会被对这个房间里的所有人删除，而且无法恢复。",
      sidebarDeleteWorkspaceSuccess: "房间已删除。",
      sidebarDeleteWorkspaceForbidden: "只有房间所有者可以删除这个房间。",
      sidebarDeleteWorkspaceNotFound: "这个房间不存在，或已经被删除。",
      sidebarLeaveWorkspace: "退出房间",
      sidebarLeaveWorkspaceConfirmTitle: "退出这个房间？",
      sidebarLeaveWorkspaceConfirm:
        "你将退出“{{name}}”。若仍有权限，之后可以再次进入。",
      sidebarWorkspaceAgents_one: "{{count}} 个智能体",
      sidebarWorkspaceAgents_other: "{{count}} 个智能体",
      sidebarWorkspaceDemoOnly: "演示",
      createWorkspacePaneTitle: "创建房间",
      createWorkspacePaneLead: "房间是本地 Agent 协同的场所。",
      createWorkspaceNameLabel: "输入房间名称",
      createWorkspaceSourceDirectory: "源目录",
      createWorkspaceSourceBrowse: "浏览…",
      createWorkspaceSourceHint: "Agent 将对此文件夹拥有读 + 写权限。",
      createWorkspaceVisibility: "可见范围",
      createWorkspaceVisibilityPrivate: "私有",
      createWorkspaceVisibilityPrivateHint: "仅你本人，不邀请他人。",
      createWorkspaceVisibilityTeam: "团队",
      createWorkspaceVisibilityTeamHint: "邀请指定成员。",
      createWorkspaceVisibilityPublic: "公开",
      createWorkspaceVisibilityPublicHint: "持有链接的用户可申请加入。",
      createFormCancel: "取消",
      createFormSubmit: "创建房间"
    },
    connector: {
      title: "连接器",
      vmManaged: "由 VM 托管",
      tabsAria: "连接器分区",
      tabMcpServers: "MCP Servers",
      tabSkills: "Skills",
      registryReadError: "无法读取本机 MCP 注册表：{{detail}}",
      statusApplyingRuntime: "正在应用 MCP 运行时配置…",
      statusMcpEnabledRuntimeErrors: "MCP 已启用，但运行时出现错误",
      installFailedForServer: "{{serverId}} 安装失败",
      statusMcpRuntimeConfigured: "MCP 运行时配置已更新",
      statusMcpRegistrySaved: "MCP 注册表已保存",
      statusSkippedUntilSignedIn: "；需登录后才启用：{{servers}}",
      statusOpeningFigmaSignIn: "正在打开 Figma 登录…",
      statusFigmaConnected: "Figma 已连接",
      statusFigmaDisconnected: "已断开 Figma，并从 Agent 中移除",
      statusOpeningGoogleSignIn: "正在打开 Google 登录…",
      statusGoogleConnected: "Google 已连接",
      statusGoogleDisconnected: "已断开 Google 连接器，并从 Agent 中移除",
      statusOpeningNotionSignIn: "正在打开 Notion 登录…",
      statusNotionConnected: "Notion 已连接",
      statusNotionDisconnected: "已断开 Notion，并从 Agent 中移除",
      builtinSectionTitle: "Built-in MCP",
      builtinSectionSubtitle:
        "启用内置 MCP Server 一次后，tsh 会为每个 Agent 自动注入。",
      enabledCountBadge: "已启用 {{count}} 个",
      toggleApplying: "应用中…",
      toggleEnabled: "已启用",
      toggleEnable: "启用",
      builtinFigmaInProgressSuffix: "（接入中）",
      builtinFigmaToggleUnavailable: "接入中",
      installStatusFailed: "安装失败",
      installStatusRemoteMcp: "Remote MCP",
      installStatusReadyInVm: "已在 VM 中就绪",
      authSignedIn: "已登录",
      authSignInRequired: "需要登录",
      disconnect: "断开",
      signIn: "登录",
      customServersHeader: "{{count}} 个自定义 MCP Server",
      addServerTitle: "添加 MCP Server",
      serverUntitled: "未命名 MCP Server",
      detailStdioCommand: "stdio 命令",
      detailTransportEndpoint: "{{transport}} 端点",
      emptyNoCustomServers: "暂无自定义 MCP Server。",
      addServerCta: "添加 MCP Server",
      editorNewServerTitle: "新建 MCP Server",
      editorSubtitle: "自定义 MCP Server 对所有 Agent 可用。",
      deleteServerTitle: "删除 MCP Server",
      fieldId: "ID",
      fieldName: "名称",
      fieldTransport: "Transport",
      fieldCommand: "命令",
      fieldArgs: "参数",
      fieldUrl: "URL",
      placeholderServerId: "github",
      placeholderServerName: "GitHub",
      placeholderCommand: "npx",
      placeholderArgs: "-y @modelcontextprotocol/server-github",
      placeholderUrl: "https://example.com/mcp",
      editorEmptyTitle: "未选择 MCP Server",
      skillSourceBuiltin: "内置",
      skillSourceCustom: "自定义",
      noSkillsFound: "未找到 Skill。"
    }
  },
  settingsPanel: zhCNSettingsPanel,
  websiteNode: zhCNWebsiteNode,
  terminalNode: {
    resizeWidth: "调整终端宽度",
    resizeHeight: "调整终端高度"
  },
  terminalCloseGuard: {
    title: "关闭这个终端？",
    body: "这个终端中仍有进程在运行。关闭后会停止该会话。",
    cancel: "取消",
    confirm: "关闭终端"
  },
  terminalFind: {
    placeholder: "查找…",
    previous: "上一个匹配",
    next: "下一个匹配",
    close: "关闭",
    caseSensitive: "区分大小写",
    useRegex: "使用正则表达式"
  },
  terminalNodeHeader: {
    directoryMismatch: "目录不匹配",
    directoryMismatchTitle:
      "绑定目录：{{executionDirectory}}\n原目录：{{expectedDirectory}}"
  },
  nodeDeleteDialog: {
    deleteNodes_one: "删除 {{count}} 个节点？",
    deleteNodes_other: "删除 {{count}} 个节点？",
    deleteTask: "删除任务？",
    deleteNode: "删除节点？",
    multipleDescription: "这会永久删除所选的 {{count}} 个节点。",
    taskDescriptionPrefix: "这会永久删除",
    nodeDescriptionPrefix: "这会永久删除这个 {{kind}}："
  },
  workspaceContextMenu: {
    newTerminal: "新建终端",
    newWebsite: "新建网页窗口",
    newTask: "新建任务",
    runAgent: "运行 Agent",
    runAgentBlank: "空白",
    runAgentProviderSubmenu: "选择 Agent 提供商",
    convertToTask: "转换为任务",
    clearSelection: "清除选择",
    editSelectedTask: "编辑任务…"
  },
  workspaceCanvas: {
    selectionHint_one: "已选中 {{count}} 个窗口。",
    selectionHint_other: "已选中 {{count}} 个窗口。",
    labelColorFilterAll: "显示全部",
    clearLabelColorFilter: "清除颜色过滤",
    chatPillLabel: "#general",
    chatPanelTitle: "会话",
    chatOpenPanel: "打开会话面板",
    chatPlaceholder: "房间会话尚未接入此入口，后续可接入真实会话。",
    composerStatusMessage: "房间内暂无 agent 在运行",
    composerUnreadCompletedPrompt: "有会话已完成，请查阅",
    composerUnreadFailedPrompt: "有会话需要处理，请查阅",
    composerWorkingAgentsMessage_one: "当前 {{count}} 个 agent 运行中",
    composerWorkingAgentsMessage_other: "当前 {{count}} 个 agent 运行中",
    composerWaitingAgentsMessage_one: "当前 {{count}} 个 agent 等待你处理",
    composerWaitingAgentsMessage_other: "当前 {{count}} 个 agent 等待你处理",
    composerRoomWaitingAgentsMessage_one: "房间内 {{count}} 个 agent 等待处理",
    composerRoomWaitingAgentsMessage_other:
      "房间内 {{count}} 个 agent 等待处理",
    composerMessageAriaLabel: "智能体进展播报",
    idleDisconnectOverlay: {
      title: "房间已因空闲暂停",
      description: "沙箱已自动断开以节省资源。重新进入后即可继续使用。",
      reenter: "重新进入房间",
      reconnecting: "正在重新进入…",
      reentered: "已重新进入房间"
    },
    agentActivityConversationSummaryLoadingAria: "正在加载对话流摘要",
    newWindow: "新建窗口",
    closeWindowAria: "关闭 {{title}}",
    websiteWindowTitle: "网页",
    minimizedWindows: "最小化窗口",
    nodeDockToolbarAriaLabel:
      "房间窗格：左键打开已有窗格或新建；右键菜单仅新建",
    nodeDockLabel: {
      terminal: "终端",
      debugTerminal: "调试终端",
      website: "浏览器",
      task: "任务",
      files: "文件",
      agent: "智能体",
      openclaw: "OpenClaw",
      claudeCode: "Claude",
      codex: "Codex",
      nexightAgent: "Tutti 智能体"
    },
    dockPopupAgentAvailabilitySectionAria: "{{name}} 的可用性状态",
    nodeDockContextNew: {
      files: "新建房间文件会话",
      terminal: "新建终端",
      debugTerminal: "新建调试终端",
      website: "新建浏览器",
      task: "新建任务",
      agent: "新建智能体"
    },
    zoomPercentInputAria:
      "画布缩放：输入百分比（如 91、91%）或 0–1 之间的小数比例，按 Enter 应用",
    zoomPercentInputTitle: "双击恢复为 100%",
    runtimeStatusPanel: {
      title: "房间运行状态",
      openComposerAria: "打开房间运行状态",
      close: "关闭",
      refresh: "刷新状态",
      workspaceConnection: "房间连接",
      runtimeConnection: "工作区环境",
      connected: "已连接",
      disconnected: "未连接",
      reconnecting: "重连中…",
      state: "状态",
      statusMessage: "说明",
      workspaceRoot: "房间根目录",
      linuxUser: "Linux 用户",
      provider: "提供方",
      sandboxId: "沙箱 ID",
      sandboxSession: "沙箱会话",
      terminals: "终端会话数",
      runtimeId: "运行时 ID",
      vsockRelay: "Vsock 中继",
      noData: "暂无可用的运行状态。"
    },
    runtimeWorkspaceDebug: {
      title: "运行时工作区调试",
      close: "关闭",
      refresh: "刷新",
      connected: "已连接",
      disconnected: "未连接",
      vmTab: "VM",
      vmOverview: "VM 概览",
      runtimeTrace: "VM 轨迹",
      workspaceOverview: "工作区概览",
      workspaceTrace: "工作区轨迹",
      vmBootPhases: "VM 启动阶段",
      workspaceEnterTraces: "工作区进入轨迹",
      workspaceAttachments: "工作区挂载",
      connection: "连接状态",
      state: "状态",
      healthState: "健康状态",
      imageBootSource: "启动镜像来源",
      statusMessage: "状态消息",
      diagnosticsStatusMessage: "诊断状态",
      elapsed: "阶段耗时",
      totalElapsed: "累计耗时",
      attempt: "尝试次数",
      sandboxSession: "沙箱会话",
      attachment: "挂载关系",
      attached: "已挂载",
      detached: "已分离",
      noRuntimeTrace: "暂无 VM 轨迹记录。",
      noVmBootPhases: "暂无 VM 启动阶段记录。",
      noWorkspaceEnters: "暂无工作区进入轨迹。",
      noWorkspaceEnterPhases: "暂无工作区进入阶段记录。",
      noWorkspaceTrace: "暂无工作区轨迹记录。",
      noWorkspaces: "暂无运行时工作区。",
      workspaceId: "工作区 ID",
      roomName: "房间名",
      mountState: "挂载状态",
      mountPoint: "挂载点",
      sandboxId: "沙箱 ID",
      websocketState: "WebSocket",
      createdAt: "创建时间",
      lastConnectedAt: "最近连接",
      lastDisconnectedAt: "最近断开",
      reconnectCount: "重连次数",
      lastError: "最近错误",
      vmRestartCount: "VM 重启次数"
    },
    sandboxSessionBanner: {
      reconnecting: "沙箱重连中…",
      disconnected: "沙箱已断开"
    },
    applications: {
      eyebrow: "Agent OS 应用",
      title: "应用中心",
      description: "从一个入口打开房间关键应用，并预览 Agent OS 常用工作流",
      launchHint: "Mock 应用进入房间后即可打开",
      launchMockUnavailable: "进入房间后可以打开这个 Mock 应用",
      categoryBasic: "基础",
      categoryOffice: "办公",
      categoryCreation: "创作",
      installAction: "安装",
      comingSoonAction: "即将上线",
      installedAction: "已安装",
      issueTitle: "任务中心",
      issueDescription: "创建、分派、运行并验收房间内的任务",
      vibeDesignTitle: "Vibe 设计",
      vibeDesignDescription: "生成产品界面、交互状态和视觉方向草案",
      vibeVideoTitle: "视频创作",
      vibeVideoDescription: "组织脚本、分镜、剪辑和最终视频方向",
      imageGenerationTitle: "图片生成",
      imageGenerationDescription: "创建视觉概念稿、生成图片素材",
      textEditorTitle: "文档",
      textEditorDescription: "编写和整理房间笔记、提示词与轻量文档",
      pptTitle: "PPT",
      pptDescription: "创建演示大纲、幻灯片草稿",
      sheetTitle: "表格",
      sheetDescription: "整理结构化数据、表格、计算和房间计划清单",
      calendarTitle: "日程",
      calendarDescription: "规划房间里程碑、跟进任务、评审和共享日程",
      systemMonitorTitle: "系统监控",
      systemMonitorDescription: "查看运行时健康度、资源信号和工作区活动",
      codeEditorTitle: "代码编辑器",
      codeEditorDescription: "打开源码文件、审阅变更并整理实现备注",
      chatTitle: "聊天",
      chatDescription: "协同房间对话、Agent 交接和快速决策",
      mockWindowStatus: "Mock 应用",
      mockWindowReady: "等待后续接入",
      mockDesignPreviewTitle: "设计看板",
      mockDesignPreviewBody: "这里将展示布局参考、组件状态和评审备注",
      mockVideoPreviewTitle: "视频时间线",
      mockVideoPreviewBody: "这里将展示场景、片段、旁白和导出状态",
      mockImageGenerationPreviewTitle: "图片工作室",
      mockImageGenerationPreviewBody:
        "这里将展示提示词、生成预览、变体和导出记录",
      mockEditorPreviewTitle: "草稿文档",
      mockEditorPreviewBody:
        "可以在这个占位编辑器里组织房间笔记和 Agent 提示词",
      mockPresentationPreviewTitle: "幻灯片 Deck",
      mockPresentationPreviewBody: "这里将展示幻灯片大纲、叙事节奏和评审备注",
      mockSheetPreviewTitle: "数据表格",
      mockSheetPreviewBody: "这里将展示表格、计算、筛选和房间计划数据",
      mockCalendarPreviewTitle: "房间日历",
      mockCalendarPreviewBody: "这里将展示里程碑、会议、跟进任务和共享日程块",
      mockMonitorPreviewTitle: "运行时信号",
      mockMonitorPreviewBody: "这里将展示 CPU、内存、网络和工作区活动信号",
      mockCodePreviewTitle: "源码工作区",
      mockCodePreviewBody: "这里将展示文件、Diff、符号和实现备注",
      mockChatPreviewTitle: "房间聊天",
      mockChatPreviewBody: "这里将展示对话线程、Agent 回复和共享决策"
    },
    vmStatusIndicator: {
      healthy: "VM 已启动且健康检测正常",
      pending: "VM 已启动，正在健康检测",
      unhealthy: "VM 启动失败或健康检测失败"
    }
  },
  labelColors: {
    title: "颜色标注",
    autoInherit: "自动（跟随分区）",
    none: "无",
    gray: "灰色",
    red: "红色",
    orange: "橙色",
    yellow: "黄色",
    green: "绿色",
    blue: "蓝色",
    purple: "紫色"
  },
  messages: zhCNMessages
} as const satisfies TranslationDictionary;
